import React, { useMemo, useState } from 'react';
import Badge from '../../../components/ui/Badge';
import { ChevronDownIcon } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { ReferenceDataGeneralList, type ReferenceInboxColumn } from '../ReferenceDataGeneralList';
import { moduleConfig } from './config';
import {
  useCrewWageLookups,
  useCrewWageScales,
  type CrewWageScale,
} from './useData';
import type { CrewContractColumn } from '../crew-wage-columns/useData';

const TYPE_ORDER: Record<string, number> = { EARNING: 0, DEDUCTION: 1, INFO: 2 };

function formatAmt(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === '') return '';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);
}

const TextCell: React.FC<{ value: string | null | undefined; mono?: boolean }> = ({ value, mono }) => {
  const { colors } = useTheme();
  return (
    <span className={cn('text-sm whitespace-nowrap', mono && 'font-mono text-xs', colors.text)}>
      {value || ''}
    </span>
  );
};

function amountForScale(row: CrewWageScale, columnId: number) {
  const hit = row.values?.find((v) => v.columnId === columnId);
  return hit?.amount;
}

/** Earnings − deductions for columns marked include-in-total (same as scale form). */
function computeScaleTotal(row: CrewWageScale, wageColumns: CrewContractColumn[]) {
  const byId = new Map(wageColumns.map((c) => [c.id, c]));
  let total = 0;
  for (const v of row.values ?? []) {
    const col = byId.get(v.columnId) ?? v.column;
    if (!col || !col.includeInTotal || col.columnType === 'INFO') continue;
    const n = typeof v.amount === 'number' ? v.amount : Number(v.amount);
    if (!Number.isFinite(n)) continue;
    total += col.columnType === 'DEDUCTION' ? -n : n;
  }
  return Math.round(total * 100) / 100;
}

const AmountCell: React.FC<{
  value: string;
  muted?: boolean;
  deduction?: boolean;
  title?: string;
  emphasize?: boolean;
}> = ({ value, muted, deduction, title, emphasize }) => {
  const { colors } = useTheme();
  return (
    <span
      className={cn(
        'block text-right text-sm tabular-nums whitespace-nowrap',
        emphasize && 'font-semibold',
        muted
          ? colors.textMuted
          : deduction
            ? 'text-red-600 dark:text-red-400'
            : colors.text,
      )}
      title={title}
    >
      {value}
    </span>
  );
};

const DateCell: React.FC<{ value?: string | null; formatDate: (s?: string) => string }> = ({
  value,
  formatDate,
}) => {
  const { colors } = useTheme();
  return (
    <span className={cn('whitespace-nowrap text-sm', colors.textMuted)}>
      {formatDate(value ?? undefined)}
    </span>
  );
};

function buildColumns(
  formatDate: (dateString?: string) => string,
  wageColumns: CrewContractColumn[],
): ReferenceInboxColumn<CrewWageScale>[] {
  const base: ReferenceInboxColumn<CrewWageScale>[] = [
    {
      key: 'vessel.vesselName',
      header: 'Vessel',
      sortable: true,
      render: (row) => <TextCell value={row.vessel?.vesselName} />,
    },
    {
      key: 'rank.name',
      header: 'Rank',
      sortable: true,
      render: (row) => <TextCell value={row.rank?.abrv || row.rank?.name} />,
    },
    {
      key: 'nationality.name',
      header: 'Nationality',
      sortable: true,
      render: (row) => <TextCell value={row.nationality?.name} />,
    },
    {
      key: 'currency',
      header: 'Curr.',
      sortable: true,
      className: 'whitespace-nowrap',
      render: (row) => <TextCell value={row.currency} mono />,
    },
  ];

  const amountCols: ReferenceInboxColumn<CrewWageScale>[] = wageColumns.map((col) => ({
    key: `col_${col.id}`,
    header: col.code || col.name,
    sortable: false,
    align: 'right' as const,
    className: 'whitespace-nowrap',
    render: (row) => {
      const amt = amountForScale(row, col.id);
      const formatted = formatAmt(amt);
      return (
        <AmountCell
          value={formatted}
          muted={!formatted}
          deduction={col.columnType === 'DEDUCTION' && Boolean(formatted)}
          title={col.name}
        />
      );
    },
  }));

  const totalCol: ReferenceInboxColumn<CrewWageScale> = {
    key: 'computedTotal',
    header: 'Total',
    sortable: false,
    align: 'right',
    className: 'whitespace-nowrap',
    render: (row) => (
      <AmountCell
        value={formatAmt(computeScaleTotal(row, wageColumns))}
        emphasize
        title="Earnings minus deductions (include-in-total columns)"
      />
    ),
  };

  const tail: ReferenceInboxColumn<CrewWageScale>[] = [
    {
      key: 'isActive',
      header: 'Active',
      sortable: true,
      className: 'whitespace-nowrap',
      render: (row) => (
        <Badge
          variant={row.isActive ? 'success' : 'danger'}
          className="shrink-0 border rounded-md px-2 py-0.5"
        >
          {row.isActive ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      key: 'effectiveFrom',
      header: 'From',
      sortable: true,
      className: 'whitespace-nowrap',
      render: (row) => <DateCell value={row.effectiveFrom} formatDate={formatDate} />,
    },
    {
      key: 'effectiveTo',
      header: 'To',
      sortable: true,
      className: 'whitespace-nowrap',
      render: (row) => <DateCell value={row.effectiveTo} formatDate={formatDate} />,
    },
  ];

  return [...base, ...amountCols, totalCol, ...tail];
}

const List: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { lookups } = useCrewWageLookups();
  const [vesselId, setVesselId] = useState('');

  const vesselOptions = useMemo(() => {
    const vessels = lookups?.vessels ?? [];
    return vessels
      .slice()
      .sort((a, b) => a.vesselName.localeCompare(b.vesselName));
  }, [lookups?.vessels]);

  const wageColumns = useMemo(() => {
    const cols = lookups?.contractColumns ?? [];
    return cols
      .filter((c) => c.isActive !== false)
      .slice()
      .sort(
        (a, b) =>
          (TYPE_ORDER[a.columnType] ?? 9) - (TYPE_ORDER[b.columnType] ?? 9) ||
          (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
          a.name.localeCompare(b.name),
      );
  }, [lookups?.contractColumns]);

  const columns = useMemo(
    () => buildColumns(moduleConfig.utils.formatDate, wageColumns),
    [wageColumns],
  );

  const extraFilters = useMemo(
    () => (vesselId ? { vesselId: [vesselId] } : undefined),
    [vesselId],
  );

  return (
    <ReferenceDataGeneralList
      moduleConfig={moduleConfig}
      useData={useCrewWageScales}
      columns={columns}
      defaultSort={{ key: 'vessel.vesselName', direction: 'asc' }}
      newLabel="New wage scale"
      getRowLabel={(row) =>
        `${row.vessel?.vesselName ?? `Vessel #${row.vesselId}`} — ${row.rank?.name ?? `Rank #${row.rankId}`}`
      }
      loadingLabel="Loading wage scales…"
      extraFilters={extraFilters}
      hasExtraFilters={Boolean(vesselId)}
      onClearExtraFilters={() => setVesselId('')}
      tabTrailing={
        <>
          <span
            className={cn('mx-1 my-1.5 w-px shrink-0 self-stretch', isDark ? 'bg-white/15' : 'bg-slate-300/80')}
            aria-hidden
          />
          <label
            className={cn(
              'relative inline-flex max-w-[14rem] items-center rounded-lg transition',
              vesselId
                ? cn(colors.surface, 'shadow-sm ring-1', isDark ? 'ring-white/10' : 'ring-black/5')
                : colors.interactiveHover,
            )}
          >
            <span className="sr-only">Filter by vessel</span>
            <select
              value={vesselId}
              onChange={(e) => setVesselId(e.target.value)}
              className={cn(
                'appearance-none cursor-pointer rounded-lg border-0 bg-transparent py-2 pl-3 pr-8 text-sm font-medium outline-none',
                'max-w-[14rem] truncate focus:ring-2 focus:ring-indigo-500/25',
                vesselId ? colors.text : colors.textMuted,
              )}
              title="Filter by vessel"
            >
              <option value="">All vessels</option>
              {vesselOptions.map((v) => (
                <option key={v.id} value={String(v.id)}>
                  {v.vesselName}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              className={cn(
                'pointer-events-none absolute right-2 h-3.5 w-3.5',
                vesselId ? colors.primaryFg : 'opacity-50',
              )}
            />
          </label>
        </>
      }
    />
  );
};

export default List;
